/**
 * AchatsDemandesPubliques.js
 * Page ultra professionnelle pour l'achat de demandes publiques COOPER'S
 * Design moderne avec statistiques, filtres avancés et cartes premium
 */

import React, { useState, useEffect, useMemo } from 'react';

import { toast } from 'sonner';
import { CheckCircle, Clock, CreditCard, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken, getAuthHeader as getStoredAuthHeader } from '../../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// V37.28 — Activités professionnelles (doit correspondre à la constante
// `ACTIVITES_PROFESSIONNELLES` de GestionTarifsDemandesPubliques.js).
const ACTIVITES_PROFESSIONNELLES = [
  { value: 'btp', label: 'BTP / Construction', icon: '🏗️' },
  { value: 'commerce', label: 'Commerce / Distribution', icon: '🏪' },
  { value: 'industrie', label: 'Industrie / Manufacturing', icon: '🏭' },
  { value: 'agriculture', label: 'Agriculture / Élevage', icon: '🌾' },
  { value: 'services', label: 'Services / Prestations', icon: '🛠️' },
  { value: 'transport', label: 'Transport / Logistique', icon: '🚚' },
  { value: 'restauration', label: 'Restauration / Hôtellerie', icon: '🍽️' },
  { value: 'sante', label: 'Santé / Médico-social', icon: '⚕️' },
  { value: 'education', label: 'Éducation / Formation', icon: '🎓' },
  { value: 'numerique', label: 'Numérique / IT', icon: '💻' },
  { value: 'artisanat', label: 'Artisanat', icon: '🎨' },
  { value: 'tourisme', label: 'Tourisme / Loisirs', icon: '🏝️' },
  { value: 'energie', label: 'Énergie / Environnement', icon: '⚡' },
  { value: 'immobilier', label: 'Immobilier', icon: '🏢' },
  { value: 'finance', label: 'Finance / Assurance', icon: '💰' },
  { value: 'autre', label: 'Autre', icon: '📋' },
];

// V37.28 — Extrait l'array d'activites d'une demande (compat retro string)
const getDemandeActivites = (d) => {
  if (Array.isArray(d?.activites) && d.activites.length > 0) {
    return d.activites.map(v => (v || '').toString().toLowerCase().trim()).filter(Boolean);
  }
  const raw = (d?.secteur || '').toString().trim().toLowerCase();
  if (!raw || raw === 'general') return [];
  return raw.split(/[,;|]/).map(s => s.trim()).filter(Boolean);
};

export function useAchats() {
  const navigate = useNavigate();
  const [demandes, setDemandes] = useState([]);
  const [demandesFiltrees, setDemandesFiltrees] = useState([]);
  const [mesAchats, setMesAchats] = useState([]);
  const [achatsFiltres, setAchatsFiltres] = useState([]);
  const [tarifInfo, setTarifInfo] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showAchatModal, setShowAchatModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('disponibles');
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [filtreType, setFiltreType] = useState('tous');
  const [filtreTerritoire, setFiltreTerritoire] = useState('tous');
  const [filtrePeriode, setFiltrePeriode] = useState('tout');
  // V37.28 — Filtre multi-select activités professionnelles
  const [filtreActivites, setFiltreActivites] = useState([]);
  const [showActivitesPanel, setShowActivitesPanel] = useState(false);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [showFiltresAvances, setShowFiltresAvances] = useState(false);

  // Remise en Communityplace
  const [showRemiseModal, setShowRemiseModal] = useState(false);
  const [remiseAchat, setRemiseAchat] = useState(null);
  const [remiseEligibility, setRemiseEligibility] = useState(null);
  const [remiseMotif, setRemiseMotif] = useState('');
  const [remiseNote, setRemiseNote] = useState('');
  const [remiseLoading, setRemiseLoading] = useState(false);
  const [remiseSubmitting, setRemiseSubmitting] = useState(false);

  // Statistiques calculées
  const stats = useMemo(() => {
    const totalDisponibles = demandes.length;
    const totalAchats = mesAchats.length;
    const achatsConfirmes = mesAchats.filter(a => a.statut === 'paiement_confirme').length;
    const achatsEnAttente = mesAchats.filter(a => ['en_attente_virement', 'en_attente_paiement_cb'].includes(a.statut)).length;
    const montantTotal = mesAchats.reduce((sum, a) => sum + (a.montant_ttc || 0), 0);
    
    return { totalDisponibles, totalAchats, achatsConfirmes, achatsEnAttente, montantTotal };
  }, [demandes, mesAchats]);

  // Types et territoires uniques
  const typesDisponibles = useMemo(() => {
    const types = new Set(demandes.map(d => d.type_demande || d.type || 'Non spécifié'));
    return ['tous', ...Array.from(types)];
  }, [demandes]);

  const territoiresDisponibles = useMemo(() => {
    const territoires = new Set(demandes.map(d => d.territoire || 'Non spécifié'));
    return ['tous', ...Array.from(territoires)];
  }, [demandes]);

  useEffect(() => {
    fetchData();
  }, []);

  // Appliquer les filtres
  useEffect(() => {
    let filtered = [...demandes];
    
    // Filtre recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d => 
        (d.nom_entreprise || '').toLowerCase().includes(query) ||
        (d.type_demande || d.type || '').toLowerCase().includes(query) ||
        (d.territoire || '').toLowerCase().includes(query) ||
        (d.description || '').toLowerCase().includes(query)
      );
    }
    
    // Filtre type
    if (filtreType !== 'tous') {
      filtered = filtered.filter(d => (d.type_demande || d.type) === filtreType);
    }
    
    // Filtre territoire
    if (filtreTerritoire !== 'tous') {
      filtered = filtered.filter(d => d.territoire === filtreTerritoire);
    }

    // V37.28 — Filtre multi-select activités professionnelles
    if (filtreActivites && filtreActivites.length > 0) {
      const wanted = new Set(filtreActivites.map(v => (v || '').toLowerCase()));
      filtered = filtered.filter(d => {
        const acts = getDemandeActivites(d);
        // Match si au moins une activité de la demande est dans le filtre
        return acts.some(a => wanted.has(a));
      });
    }
    
    // Filtre période
    if (filtrePeriode !== 'tout') {
      const now = new Date();
      filtered = filtered.filter(d => {
        const date = new Date(d.created_at);
        switch (filtrePeriode) {
          case 'aujourd-hui':
            return date.toDateString() === now.toDateString();
          case 'semaine':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return date >= weekAgo;
          case 'mois':
            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      });
    }
    
    setDemandesFiltrees(filtered);
  }, [demandes, searchQuery, filtreType, filtreTerritoire, filtrePeriode, filtreActivites]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      // Récupérer territoire + type_entreprise du user courant pour filtrer
      // les tarifs désactivés (config admin)
      let qs = '';
      try {
        const meRes = await fetch(`${API_URL}/api/auth/me`, { headers, credentials: 'include' });
        if (meRes.ok) {
          const me = await meRes.json();
          const territoire = me?.territoire || me?.region;
          const typeEntreprise = me?.type_entreprise || me?.entreprise_type;
          const params = new URLSearchParams();
          if (territoire) params.append('territoire', territoire);
          if (typeEntreprise) params.append('type_entreprise', typeEntreprise);
          const s = params.toString();
          if (s) qs = `?${s}`;
        }
      } catch {/* fallback : pas de filtres */}

      const [tarifRes, demandesRes, achatsRes, walletRes] = await Promise.all([
        fetch(`${API_URL}/api/achats/tarif-achat${qs}`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/achats/demandes-publiques/disponibles`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/achats/mes-achats`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/wallets/me`, { headers }).catch(() => ({ ok: false })),
      ]);
      
      if (tarifRes.ok) setTarifInfo(await tarifRes.json());
      if (demandesRes.ok) {
        const data = await demandesRes.json();
        // V37.08 — Tri défensif : plus récentes en premier (au cas où le backend ne trie pas)
        const sorted = [...data].sort((a, b) => {
          const da = new Date(a.created_at || 0).getTime();
          const db = new Date(b.created_at || 0).getTime();
          return db - da;
        });
        setDemandes(sorted);
        setDemandesFiltrees(sorted);
      }
      if (achatsRes.ok) {
        const data = await achatsRes.json();
        setMesAchats(data);
        setAchatsFiltres(data);
      }
      if (walletRes.ok) {
        const w = await walletRes.json();
        setWalletBalance(Number(w?.credit_balance || w?.balance || 0));
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleAchat = async (demande, methodePaiement) => {
    try {
      const token = getToken();
      // V35.63 — Mémoriser la page d'origine pour que le bouton "Retour"
      // des pages de succès/cancel Stripe revienne ici au lieu du dashboard.
      try {
        sessionStorage.setItem('payment_return_path', window.location.pathname + window.location.search);
      } catch {}
      const response = await fetch(`${API_URL}/api/achats/demandes-publiques/${demande.id}/acheter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          methode_paiement: methodePaiement,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          // Redirection vers Stripe pour paiement CB
          window.location.href = data.url;
        } else {
          toast.success('Achat initié avec succès !');
          setShowAchatModal(false);
          fetchData();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'achat');
      }
    } catch {
      toast.error('Erreur lors de l\'achat');
    }
  };

  const getStatutConfig = (statut) => {
    const configs = {
      'en_attente_virement': { 
        label: 'Attente virement', 
        color: 'bg-amber-500 text-white border-amber-600 shadow-sm',
        icon: Clock 
      },
      'en_attente_paiement_cb': { 
        label: 'Attente paiement CB', 
        color: 'bg-blue-600 text-white border-blue-700 shadow-sm',
        icon: CreditCard 
      },
      'paiement_confirme': { 
        label: 'Confirmé', 
        color: 'bg-emerald-600 text-white border-emerald-700 shadow-sm',
        icon: CheckCircle 
      }
    };
    return configs[statut] || { label: statut, color: 'bg-slate-600 text-white', icon: AlertCircle };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant || 0);
  };

  const resetFiltres = () => {
    setSearchQuery('');
    setFiltreType('tous');
    setFiltreTerritoire('tous');
    setFiltrePeriode('tout');
    setFiltreActivites([]);
  };

  // V37.28 — Toggle d'une activité dans le filtre multi-select
  const toggleActiviteFilter = (value) => {
    setFiltreActivites((prev) => {
      const set = new Set(prev || []);
      if (set.has(value)) set.delete(value); else set.add(value);
      return Array.from(set);
    });
  };

  const hasActiveFilters = searchQuery || filtreType !== 'tous' || filtreTerritoire !== 'tous' || filtrePeriode !== 'tout' || (filtreActivites && filtreActivites.length > 0);

  // ============================================
  // REMISE EN COMMUNITYPLACE
  // ============================================
  const openRemiseModal = async (achat) => {
    setRemiseAchat(achat);
    setRemiseMotif('');
    setRemiseNote('');
    setRemiseEligibility(null);
    setRemiseLoading(true);
    setShowRemiseModal(true);
    try {
      // V38.12 — Bascule vers axios (XHR) pour éviter toute interférence
      // avec un Service Worker obsolète qui clonerait la réponse fetch().
      const { data } = await axios.get(
        `${API_URL}/api/achats/demandes-publiques/remises/eligibility/${achat.demande_id}`,
        { headers: getStoredAuthHeader() },
      );
      setRemiseEligibility(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
      toast.error(`Impossible de calculer la pénalité : ${msg}`);
      setShowRemiseModal(false);
    } finally {
      setRemiseLoading(false);
    }
  };

  const submitRemise = async () => {
    if (!remiseAchat || !remiseMotif.trim()) {
      toast.error('Veuillez choisir un motif');
      return;
    }
    setRemiseSubmitting(true);
    try {
      // V38.12 — Bascule vers axios (XHR) pour éviter toute interférence
      // avec un Service Worker obsolète qui clonerait la réponse fetch()
      // ("Failed to execute 'clone' on 'Response': Response body is already used").
      const { data } = await axios.post(
        `${API_URL}/api/achats/demandes-publiques/${remiseAchat.demande_id}/remettre-communityplace`,
        { motif: remiseMotif, note: remiseNote },
        { headers: { ...getStoredAuthHeader(), 'Content-Type': 'application/json' } },
      );
      const penalty = Number(data?.penalty_credits || 0);
      toast.success(
        penalty > 0
          ? `Demande remise. Pénalité débitée : ${penalty} crédits. L'équipe O'SCOP a été notifiée.`
          : 'Demande remise dans la Communityplace. L\'équipe O\'SCOP a été notifiée.',
      );
      setShowRemiseModal(false);
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === 'object' && detail?.error) {
        msg = `${detail.error} (solde: ${detail.balance}, requis: ${detail.required})`;
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (e?.response?.status) {
        msg = `HTTP ${e.response.status}`;
      } else {
        msg = e?.message || 'Erreur réseau';
      }
      toast.error(`Échec remise : ${msg}`);
    } finally {
      setRemiseSubmitting(false);
    }
  };

  return { achatsFiltres, activeTab, dateDebut, dateFin, demandes, demandesFiltrees, fetchData, filtreActivites, filtrePeriode, filtreTerritoire, filtreType, formatDate, formatMontant, getStatutConfig, handleAchat, hasActiveFilters, loading, mesAchats, navigate, openRemiseModal, remiseAchat, remiseEligibility, remiseLoading, remiseMotif, remiseNote, remiseSubmitting, resetFiltres, searchQuery, selectedDemande, setAchatsFiltres, setActiveTab, setDateDebut, setDateFin, setDemandes, setDemandesFiltrees, setFiltreActivites, setFiltrePeriode, setFiltreTerritoire, setFiltreType, setLoading, setMesAchats, setRemiseAchat, setRemiseEligibility, setRemiseLoading, setRemiseMotif, setRemiseNote, setRemiseSubmitting, setSearchQuery, setSelectedDemande, setShowAchatModal, setShowActivitesPanel, setShowDetailsModal, setShowFiltresAvances, setShowRemiseModal, setTarifInfo, setWalletBalance, showAchatModal, showActivitesPanel, showDetailsModal, showFiltresAvances, showRemiseModal, stats, submitRemise, tarifInfo, territoiresDisponibles, toggleActiviteFilter, typesDisponibles, walletBalance };
}
